<script setup>
import { useI18n } from 'vue-i18n'

const { $confirm, $notify, $escapeXml } = useNuxtApp()
const { loggedIn } = useUserSession()
const { Settings } = useSettings()

const { post, preview, card } = defineProps({ post: Object, preview: Boolean, card: Boolean })
const { locale, t } = useI18n()

const date = computed(() => new Date(post?.date || Date.now()).toLocaleDateString(locale.value, {
  year: 'numeric',
  month: 'long',
  day: 'numeric' }))

async function onShare() {
  const text = post.URL
  try {
    await navigator.clipboard.writeText(text)
  }
  catch (error) {
    console.error(error.message)
    $notify(t('Error: your browser is unsupported'), 'error')
    return
  }
  $notify(t('Link copied'), 'success')
}

async function toggleVisibility() {
  const action = post.visible ? 'confirm-post-hide' : 'confirm-post-show'
  const ret = await $confirm(t(action, { title: $escapeXml(post.title) }))
  if (!ret) return
  try {
    await $fetch(`/api/post/detail/${post.id}`, { method: 'PATCH', body: { visible: !post.visible } })
    emit('toggleVisibility')
  }
  catch (e) {
    $notify('Error! ' + e)
  }
}

// most images are already webp; however, this has not always been the case, so
// old images are available in both jpeg and webp format. We don't care about
// supporting jpeg anymore, so we'll only ship webp to the user

const image = computed(() => '/media/' + (post?.image ?? post?.source?.image ?? 'fallbackImage.png').replace(/.jpg$/, '.webp'))

const emit = defineEmits(['remove', 'toggleVisibility', 'edit', 'pinToggled'])

async function remove() {
  const ret = await $confirm(t('confirm-post-remove', { title: $escapeXml(post.title) }))
  if (!ret) return
  try {
    await $fetch(`/api/post/${post.id}`, { method: 'DELETE' })
    $notify('Removed', 'success')
    emit('remove')
  }
  catch (e) {
    $notify('Error removing post: ' + error.value.statusError)
  }
}

async function pinToggle() {
  const action = post.pinned ? 'confirm-post-unpin' : 'confirm-post-pin'
  const ret = await $confirm(t(action, { title: $escapeXml(post.title) }))
  if (!ret) return
  try {
    await $fetch(`/api/post/pin/${post.id}`, { method: 'PUT' })
    emit('pinToggled')
  }
  catch (e) {
    $notify('Error pinning post: ' + error.value.statusError)
  }
}
</script>

<template>
<!-- :style="{ boxShadow: `inset 0px 5px 500px ${post?.source?.color ?? '#ddd'}`}" -->
<!-- :style="{ border: `1px solid ${post?.source?.color ?? '#ddd'}`}" -->
<article
  class="post"
  :class="{ hidden: post?.visible === false, card, [`source_${post?.source?.id}`]: true }"
  :style="{ border: `1px solid ${post?.source?.color || '#ccc'}` }"
>
  <!-- if imageWebp doesn't exist, provide an error fallback image -->
  <nuxt-link
    :to="post.URL"
    class="media"
    target="_blank"
  >
    <v-img
      aspect-ratio="4/3"
      height="155"
      :src="image"
      rounded="0"
      cover
    >
      <template v-slot:error>
        <v-img src="/media/fallbackImage.png" />
      </template>
    </v-img>
  </nuxt-link>

  <section class="content">
    <div class="title-and-summary justify-space-between">
      <nuxt-link
        :href="post.URL"
        target="_blank"
        class="text-h6 font-weight-bold text-secondary"
      ><span v-text="post.title" /></nuxt-link>
      <div
        class="summary text-caption text-medium-emphasis"
        v-text="post.summary"
      />
    </div>
    <div
      v-if="loggedIn || !Settings.showTagsToAdminOnly"
      class="tags"
    >
      <v-chip
        v-for="tag in post?.tags?.slice(0, 5)"
        :key="tag.id"
        label
        :to="`/tag/${tag.id}`"
        size="x-small"
        class="mr-1"
      >
        {{ tag.name }}
      </v-chip>
    </div>
    <div class="source-and-actions d-flex flex-row flex-wrap justify-space-between">
      <div class="source-date-and-tags d-flex flex-wrap mt-2 flex-column">
        <div class="source-and-date font-weight-light text-caption">
          {{ date }}
        </div>
        <nuxt-link
          class="font-weight-light text-caption"
          :to="`/s/${post.source.id}`"
        >{{ post.source.name || post.source.link }}</nuxt-link>
      </div>
      <v-card-actions
        v-if="!loggedIn && !preview"
        class="actions"
        density="compact"
        size="small"
      >
        <v-btn
          icon="mdi-text"
          variant="text"
          alt="open post"
          size="small"
          :to="`/p/${post.id}`"
        />
        <v-btn
          icon="mdi-share"
          variant="text"
          :alt="t('Share post')"
          size="small"
          color="primary"
          @click="onShare"
        />
      </v-card-actions>
    </div>
    <template v-if="loggedIn">
      <v-card-actions
        v-if="!preview"
        class="actions justify-space-between"
        density="compact"
        size="small"
      >
        <v-btn
          icon="mdi-text"
          variant="text"
          alt="open post"
          density="compact"
          size="small"
          :to="`/p/${post.id}`"
        />

        <v-btn
          icon="mdi-delete"
          color="red"
          variant="text"
          density="compact"
          size="small"
          @click="remove"
        />
        <v-btn
          :icon="post?.visible ? 'mdi-eye-off' : 'mdi-eye'"
          :color="post?.visible ? 'gray' : 'orange' "
          variant="text"
          density="compact"
          size="small"
          @click="toggleVisibility"
        />
        <v-btn
          icon="mdi-pencil"
          color="info"
          variant="text"
          density="compact"
          size="small"
          @click="emit('edit', post)"
        />
        <v-btn
          icon="mdi-pin"
          :color="post.pinned ? 'success' : 'gray'"
          variant="text"
          density="compact"
          size="small"
          @click="pinToggle"
        />
      </v-card-actions>
    </template>
  </section>
</article>
</template>

<style>
.theme-Blob .post {
  flex-wrap: wrap;
  flex-direction: row;
}

.theme-Grid .post {
  flex-direction: column;
}

.theme-Grid .post .content {
  justify-content: space-between;
  align-content: space-between;
  flex-grow: 1;
}

.theme-Blob .post .content {
  flex: 2 1 600px;
}

.post{

  position: relative;
  display: flex;

  background-color: #f3f3f3;
  border: 1px solid #ddd;

  /* max-height: 155px; */
  overflow: hidden;
  /* min-height: 200px; */

  width: 100%;
}
/*
.card {
  flex-direction: column;
  width: 380px;
  flex-grow: 1;
} */

.post .navbar {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  height: 32px;
  overflow: hidden;
}

.post .navbar .tags {
  padding-top: 3px;
}

.post .media {
  /* width: 200px; */
  max-height: 155px;
  overflow: hidden;
  flex: 2 1 200px;
  background: none;
  height: 100%;
}

.media picture,
.media img {
  aspect-ratio: 1.7778;
  object-fit: cover;
  height: 100%;
  width: 100%;
}

.hidden {
  opacity: 0.4;
}

.post .content {
  overflow: hidden;
  padding: 5px 10px 5px 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  /* max-height: 155px; */
}

.content .summary {
  max-height: 60px;
  overflow: hidden;
  flex-grow: 1;
  text-overflow: ellipsis;
}

.content a {
  color: rgba(var(--v-theme-primary));
}

.title-and-summary {
flex-grow: 1;
}
</style>
